import { useEffect, useRef, useState } from 'react';
import { Formik, type FormikProps } from 'formik';
import { DateTime as DT } from 'luxon';
import type {
    LeadCloudFileType,
    LeadType,
    ObjectType,
    TaskCreateFormType,
    UserType,
} from '@econtractor/types';
import NewTaskForm from './NewTaskForm';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import { useTranslation } from 'react-i18next';
import * as apiTask from 'api/apiTask';
import * as apiLeads from 'api/apiLeads';
import * as apiObjects from 'api/apiObjects';
import { getCloudflareUploadUrls, linkCloudflareFiles } from 'api/apiCloudflare';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import {
    getInitNewTaskStruct,
    normalizeNewTaskValues,
} from './helpers/initNewTaskStruct';

interface NewTaskFormikProps {
    show: boolean;
    onHide: () => void;
    onCreated?: () => void;
    leadId?: number | null;
    objectId?: number | null;
    /** Назва заявки/об'єкта для миттєвого префілу до завантаження з API */
    contextName?: string | null;
}

const stubLead = (leadId: number, name?: string | null): LeadType =>
    ({
        ID: Number(leadId),
        clientName: String(name || '').trim() || `#${leadId}`,
        clientPhone: '',
        clientEmail: '',
        leadStatus: 0,
    }) as LeadType;

const stubObject = (objectId: number, name?: string | null): ObjectType =>
    ({
        ID: Number(objectId),
        objectName: String(name || '').trim() || `#${objectId}`,
        clientName: '',
    }) as ObjectType;

const NewTaskFormik = ({
    show,
    onHide,
    onCreated,
    leadId = null,
    objectId = null,
    contextName = null,
}: NewTaskFormikProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const { showToast } = useToast();
    const formikRef = useRef<FormikProps<TaskCreateFormType>>(null);
    const currentUser = (auth?.user || null) as UserType | null;
    const [pendingCloudFiles, setPendingCloudFiles] = useState<File[]>([]);
    const [cloudProgress, setCloudProgress] = useState<number | boolean>(false);
    const taskForLocked = Boolean(leadId || objectId);

    // Ініціалізація лише коли діалог відкритий; без enableReinitialize,
    // щоб ререндери батька (latency API) не скидали форму.
    useEffect(() => {
        if (!show) return;

        let cancelled = false;
        const scopeLeadId = leadId ? Number(leadId) : null;
        const scopeObjectId = objectId ? Number(objectId) : null;

        const initialLead = scopeLeadId ? stubLead(scopeLeadId, contextName) : null;
        const initialObject = scopeObjectId ? stubObject(scopeObjectId, contextName) : null;

        formikRef.current?.resetForm({
            values: getInitNewTaskStruct(
                scopeLeadId,
                scopeObjectId,
                currentUser,
                initialLead,
                initialObject,
            ),
        });
        setPendingCloudFiles([]);
        setCloudProgress(false);

        const loadContext = async () => {
            if (scopeLeadId) {
                const details = await apiLeads.getLead(scopeLeadId);
                if (cancelled || !formikRef.current) return;
                const lead = details?.lead || stubLead(scopeLeadId, contextName);
                formikRef.current.setValues((prev) => ({
                    ...prev,
                    taskFor: 'lead',
                    lead,
                    object: null,
                }));
                return;
            }

            if (scopeObjectId) {
                const details = await apiObjects.getObject(scopeObjectId);
                if (cancelled || !formikRef.current) return;
                const object =
                    (details?.object as ObjectType | undefined) ||
                    stubObject(scopeObjectId, contextName);
                formikRef.current.setValues((prev) => ({
                    ...prev,
                    taskFor: 'object',
                    object,
                    lead: null,
                }));
            }
        };

        void loadContext();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- currentUser беремо на момент відкриття
    }, [show, leadId, objectId, contextName]);

    const uploadCloudFiles = async (
        taskId: number,
        payloadLeadId: number | null,
        payloadObjectId: number | null,
    ): Promise<LeadCloudFileType[]> => {
        if (!pendingCloudFiles.length) return [];

        const filesMeta = pendingCloudFiles.map((file) => ({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            fileSize: file.size,
        }));

        const uploadConfigs = await getCloudflareUploadUrls(filesMeta, {
            taskId: Number(taskId || 0),
            leadId: Number(payloadLeadId || 0) || undefined,
            objectId: Number(payloadObjectId || 0) || undefined,
        });
        if (!uploadConfigs || uploadConfigs.length !== pendingCloudFiles.length) {
            throw new Error('Upload URLs are not available');
        }

        const uploaded = await uploadFilesWithOverallProgress(
            pendingCloudFiles,
            uploadConfigs,
            setCloudProgress,
        );

        setCloudProgress(100);
        return uploaded.filter((item) => item.success).map((item) => item.config);
    };

    const validateForm = (values: TaskCreateFormType): boolean => {
        if (values.taskFor === 'lead' && !values.lead?.ID) {
            showToast(t('errEmptyLead'), 'danger');
            return false;
        }
        if (values.taskFor === 'object' && !values.object?.ID) {
            showToast(t('errEmptyObject'), 'danger');
            return false;
        }
        if (!String(values.taskName || '').trim()) {
            showToast(t('errEmptyName'), 'danger');
            return false;
        }
        if (!values.executor?.ID) {
            showToast(t('errEmptyExecutor'), 'danger');
            return false;
        }
        const payload = normalizeNewTaskValues(values);
        if (payload.dateEnd < DT.now().toMillis()) {
            showToast(t('errPastDateEnd'), 'danger');
            return false;
        }
        return true;
    };

    return (
        <Formik
            innerRef={formikRef}
            initialValues={getInitNewTaskStruct(
                leadId,
                objectId,
                currentUser,
                leadId ? stubLead(Number(leadId), contextName) : null,
                objectId ? stubObject(Number(objectId), contextName) : null,
            )}
            onSubmit={async (values) => {
                if (!validateForm(values)) return;

                const payload = normalizeNewTaskValues(values);
                const resCreate = await apiTask.createTask(payload);
                if (!resCreate?.stat || !resCreate?.taskId) {
                    showToast(t('common:messError'), 'danger');
                    return;
                }

                if (pendingCloudFiles.length) {
                    try {
                        setCloudProgress(1);
                        const cloudFiles = await uploadCloudFiles(
                            resCreate.taskId,
                            payload.leadId,
                            payload.objectId,
                        );
                        if (!cloudFiles.length) {
                            showToast(t('common:messError'), 'warning');
                        } else {
                            const linked = await linkCloudflareFiles(cloudFiles, {
                                taskId: Number(resCreate.taskId),
                                leadId: Number(payload.leadId || 0) || undefined,
                                objectId: Number(payload.objectId || 0) || undefined,
                            });
                            if (!linked || cloudFiles.length !== pendingCloudFiles.length) {
                                showToast(t('common:messError'), 'warning');
                            }
                        }
                    } catch (e) {
                        console.error(e);
                        showToast(t('common:messError'), 'warning');
                    } finally {
                        setCloudProgress(false);
                    }
                }

                showToast(t('messCreated'), 'success');
                setPendingCloudFiles([]);
                onCreated?.();
                onHide();
            }}
        >
            {({ handleSubmit, isSubmitting, setFieldValue, values }) => (
                <NewTaskForm
                    handleSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    setFieldValue={setFieldValue}
                    values={values}
                    pendingCloudFiles={pendingCloudFiles}
                    setPendingCloudFiles={setPendingCloudFiles}
                    cloudProgress={cloudProgress}
                    taskForLocked={taskForLocked}
                />
            )}
        </Formik>
    );
};

export default NewTaskFormik;
